// TODO: convert to a hook so we don't have to pass in user and flags
// https://palmetto.atlassian.net/browse/PA-3157

import { LDFlagKeys, LDFlags, PlatformConfig } from '.';
import { isSolarProjectType, ProjectV2Response } from '../api/project/types';
import { EnergyType, Property, SundialEnergyConnectionTypes } from '../api/properties/types';
import { User, UserSource } from '../api/user/types';
import {
  areAllProjectOrganizationsEligible,
  getInstalledSolarProject,
  isLegacySolarUser,
  isOrganizationEligible,
} from '../utils/referAFriend';
import { userHasExistingRatePlan } from '../utils/utilityRatePlan';

export type IFeatureConfig = (user?: User, flags?: LDFlags, property?: Property) => boolean;

export type FeatureConfigGroup = {
  [key: string]: ({
    user,
    flags,
    property,
    projects,
  }: {
    user?: User;
    flags?: LDFlags;
    property?: Property;
    projects?: ProjectV2Response[];
  }) => boolean;
};

export const isActiveProject = ({ stage }: Pick<ProjectV2Response, 'stage'>): boolean =>
  stage !== 'cancelled' && stage !== 'completed';

export const hasActiveSystem: IFeatureConfig = (user?: User) =>
  Boolean(user?.segmentation?.solarSystemCount) || Boolean(user?.hasCompletedProject);

const service = hasActiveSystem;

const referAFriend: IFeatureConfig = (user?: User, flags?: LDFlags) => {
  if (!flags?.referAFriend) {
    return false;
  }
  const solarSystemCount = user?.segmentation?.solarSystemCount || 0;
  const solarProjects = user?.projects?.filter(
    (project) => isSolarProjectType(project.type) && project.stage !== 'cancelled',
  );

  if (isLegacySolarUser(solarSystemCount, solarProjects)) {
    return true;
  }

  // hvac projects or luminance users
  if (!solarProjects || solarProjects.length === 0) {
    return true;
  }

  const enabledOrganizationIds =
    (flags?.referAFriendOrganizations as { sabalOrganizationIds: string[] })?.sabalOrganizationIds || [];
  if (enabledOrganizationIds.length <= 0) {
    return false;
  }

  const installedSolarProject = getInstalledSolarProject(solarProjects);
  if (installedSolarProject) {
    return isOrganizationEligible(installedSolarProject, enabledOrganizationIds);
  }

  return areAllProjectOrganizationsEligible(solarProjects, enabledOrganizationIds);
};

const demoMode: IFeatureConfig = (_user?: User, flags?: LDFlags) => Boolean(flags?.[LDFlagKeys.demoMode]);

const financeAccountDetail: IFeatureConfig = (user?: User, flags?: LDFlags) => {
  if (demoMode(user, flags)) {
    return true;
  }
  return Boolean(user && user.financeAccounts?.length);
};

export function isUtilityNotSupported(user?: User, property?: Property): boolean {
  const notSupported = user?.settings?.utilityNotSupported;
  if (!notSupported) return false;
  if (notSupported.notListed) return true;
  if (notSupported.id && property?.electricUtility && notSupported.id === property.electricUtility) return true;
  return false;
}

const displayConnectUtilityOnDashboard: IFeatureConfig = (user?, flags?, property?) => {
  const hasConsumptionDataSources = property?.sundialSite?.dataSources.some(
    (dataSource) => dataSource.sourceType === EnergyType.consumption,
  );
  const hasUtilityConnection = Boolean(user?.settings?.arcadiaPlugIntervals);
  const isUtilitySupported = !isUtilityNotSupported(user, property);

  return Boolean(!hasConsumptionDataSources && !hasUtilityConnection && isUtilitySupported);
};

export type GatedFeaturedItemName = 'heat-pump-upgrade' | 'stand-alone-storage' | 'solar';

export const COMPLETED_PROJECT_ELIGIBILITY_DAYS: Record<GatedFeaturedItemName, number> = {
  'heat-pump-upgrade': 90,
  'stand-alone-storage': 90,
  solar: 90,
};
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const BATTERY_CONNECTION_TYPES = [
  SundialEnergyConnectionTypes.batteryToHome,
  SundialEnergyConnectionTypes.batteryToGrid,
  SundialEnergyConnectionTypes.gridToBattery,
  SundialEnergyConnectionTypes.pvToBattery,
];

const hasProjectInProgress = (projects: ProjectV2Response[]): boolean => projects.some(isActiveProject);

const getProjectCompletionDate = (project: ProjectV2Response): Date | undefined => {
  const completedTimestamps = (project.stageHistory ?? [])
    .filter((item) => item.stage === 'completed')
    .map((item) => new Date(item.createdAt).getTime());
  return completedTimestamps.length > 0 ? new Date(Math.max(...completedTimestamps)) : project.createdAt;
};

const isCompletedBeyondEligibilityWindow = (project: ProjectV2Response, eligibilityDays: number): boolean => {
  const completionDate = getProjectCompletionDate(project);
  if (!completionDate) {
    return false;
  }
  return Date.now() - new Date(completionDate).getTime() > eligibilityDays * MS_PER_DAY;
};

const isEligibleForProductFeaturedItems = (
  projects: ProjectV2Response[],
  itemName: GatedFeaturedItemName,
): boolean => {
  if (hasProjectInProgress(projects)) {
    return false;
  }
  const eligibilityDays = COMPLETED_PROJECT_ELIGIBILITY_DAYS[itemName];
  return projects
    .filter((project) => project.stage === 'completed')
    .every((project) => isCompletedBeyondEligibilityWindow(project, eligibilityDays));
};

const propertyHasBatteryStorage = (property?: Property): boolean =>
  Boolean(property?.sundialSite?.connections?.some((connection) => BATTERY_CONNECTION_TYPES.includes(connection)));

const featuredItems: FeatureConfigGroup = {
  'heat-pump-upgrade': ({
    property,
    projects,
    flags,
  }: {
    property?: Property;
    projects?: ProjectV2Response[];
    flags?: LDFlags;
  }) => {
    if (demoMode(undefined, flags)) {
      return true;
    }
    if (!projects) {
      return false;
    }
    const allowedStates = new Set([
      'AL',
      'AZ',
      'CA',
      'CO',
      'DC',
      'FL',
      'GA',
      'ID',
      'IL',
      'IN',
      'KS',
      'KY',
      'LA',
      'MA',
      'MD',
      'ME',
      'MN',
      'MO',
      'NC',
      'NY',
      'NV',
      'OH',
      'OK',
      'PA',
      'SC',
      'TN',
      'TX',
      'UT',
    ]);
    const hvacAllowedStates = property?.address?.state ? allowedStates.has(property.address.state) : false;
    const hasCompletedHvacProject = projects.some(
      (project) => project.type === 'hvac' && project.stage === 'completed',
    );
    return (
      hvacAllowedStates &&
      !hasCompletedHvacProject &&
      isEligibleForProductFeaturedItems(projects, 'heat-pump-upgrade')
    );
  },
  'stand-alone-storage': ({
    property,
    projects,
    flags,
  }: {
    property?: Property;
    projects?: ProjectV2Response[];
    flags?: LDFlags;
  }) => {
    if (demoMode(undefined, flags)) {
      return true;
    }
    if (!projects) {
      return false;
    }
    const allowedStates = new Set([
      'AZ',
      'CA',
      'CO',
      'CT',
      'DC',
      'DE',
      'FL',
      'GA',
      'IL',
      'IN',
      'KY',
      'MA',
      'MD',
      'MO',
      'ME',
      'NC',
      'NJ',
      'NH',
      'OH',
      'OR',
      'PA',
      'RI',
      'SC',
      'TX',
      'VA',
      'VT',
    ]);
    const isPropertyInAllowedState = property ? Boolean(allowedStates.has(property?.address.state)) : false;
    const hasCompletedStorageProject = projects.some(
      (project) => project.type === 'energyBackup' && project.stage === 'completed',
    );

    return (
      isPropertyInAllowedState &&
      !hasCompletedStorageProject &&
      !propertyHasBatteryStorage(property) &&
      isEligibleForProductFeaturedItems(projects, 'stand-alone-storage')
    );
  },
  'rate-plan-item': ({ property, user, flags }: { property?: Property; user?: User; flags?: LDFlags }) => {
    const hasRatePlan = user && userHasExistingRatePlan(user);
    const eligibleUtilities = flags?.[LDFlagKeys.utilitiesEligibleForRatePlan] as { utilities?: string[] };
    const isUtilityEligible = property?.electricUtility
      ? eligibleUtilities?.utilities?.includes(property.electricUtility)
      : false;

    return Boolean(!hasRatePlan && isUtilityEligible);
  },
  solar: ({ user, projects }: { property?: Property; user?: User; projects?: ProjectV2Response[] }) => {
    if (!projects) {
      return false;
    }
    const hasCompletedSolarProject = projects.some(
      (project) => isSolarProjectType(project.type) && project.stage === 'completed',
    );
    const hasSolarSystem = Boolean(user?.segmentation?.solarSystemCount);
    return !hasCompletedSolarProject && !hasSolarSystem && isEligibleForProductFeaturedItems(projects, 'solar');
  },
};

const energyPage = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.energyPage);
};

const energyCarousel = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.energyCarousel]);
};

const achievementsPage = (user?: User, flags?: LDFlags, property?: Property): boolean => {
  return Boolean(flags?.[LDFlagKeys.achievementsPage]);
};

const trivia = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.triviaStartDate);
};

const tasks = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.tasks]);
};

const connectedAccountsPage = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.connectedAccountsPage]);
};

const splineHouse = (user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.splineHouse]);
};

const pointsHistory = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.pointsHistory]);
};

const solarSavings = (_user?: User, flags?: LDFlags, property?: Property): boolean => {
  const hasConsumptionDataSource = property?.sundialSite?.dataSources.some(
    (dataSource) => dataSource.sourceType === EnergyType.consumption,
  );
  return Boolean(flags?.[LDFlagKeys.solarSavings]) && Boolean(hasConsumptionDataSource);
};

const solarValue = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.solarValue]);
};

const userEmailUpdate = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.userEmailUpdate]);
};

const showGetAccountOnLoginSignupChoice = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.showGetAccountOnLoginSignupChoice]);
};

const riveBottomNav = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.riveBottomNav]);
};

const plaidDemo = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.plaidDemo]);
};

const showRateDataForSavings = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.showRateDataForSavings]);
};

const compareMultiplePeriods = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.compareMultiplePeriods]);
};

const sunnyChatEnabled = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.sunnyChatEnabled]);
};

const solarAndEnergyPage = (user?: User, flags?: LDFlags, property?: Property): boolean => {
  const sundialSite = property?.sundialSite;
  if (!sundialSite?.dataSources?.length) {
    return false;
  }

  const hasProductionSource = sundialSite.dataSources.some(
    (dataSource) => dataSource.sourceType === EnergyType.production,
  );
  const hasConsumptionSource = sundialSite.dataSources.some(
    (dataSource) => dataSource.sourceType === EnergyType.consumption,
  );

  // TODO: Remove solarSystemActivationDate fallback after backfill script has run for all properties
  const productionTimestamp = sundialSite.firstMeasurableGenerationTimestamp || property?.solarSystemActivationDate;
  const hasProductionDataAvailable = hasProductionSource && Boolean(productionTimestamp);
  const hasConsumptionDataAvailable = hasConsumptionSource && Boolean(sundialSite.earliestReadingTimestamp);

  return hasProductionDataAvailable || hasConsumptionDataAvailable;
};

export const ARTICLE_POINTS_VALUE = 20;

const getArticlePoints = (user?: User, flags?: LDFlags): number | undefined => {
  const shouldDisplay = Boolean(flags?.[LDFlagKeys.displayPointsOnArticlePages]);
  return shouldDisplay ? ARTICLE_POINTS_VALUE : undefined;
};

export const getSingleActiveProjectId = (user?: User): string | undefined => {
  const active = (user?.projects ?? []).filter(isActiveProject);
  return active.length === 1 ? active[0]._id : undefined;
};

export const isSingleActiveProjectHome: IFeatureConfig = (user?: User): boolean =>
  !hasActiveSystem(user) && Boolean(getSingleActiveProjectId(user));

const displayHomePageV2: IFeatureConfig = (_user?: User, flags?: LDFlags): boolean =>
  Boolean(flags?.[LDFlagKeys.homePageV2]);

const displayNotificationCenter: IFeatureConfig = (_user?: User, flags?: LDFlags): boolean =>
  Boolean(flags?.[LDFlagKeys.notificationCenter]);

const displayProjectAsHome: IFeatureConfig = (user?: User, flags?: LDFlags): boolean => {
  const flagEnabled = !!flags?.[LDFlagKeys.projectPageAsHome];
  const isHomePageV2 = displayHomePageV2(user, flags);

  return flagEnabled && !isHomePageV2 && isSingleActiveProjectHome(user);
};

const displayHomePage: IFeatureConfig = (user?: User, _flags?: LDFlags, property?: Property): boolean => {
  if (user?.source === UserSource.luminance || user?.source === UserSource.organic) {
    const hasRequiredDataSources = property?.sundialSite?.dataSources.some(
      (dataSource) =>
        dataSource.sourceType === EnergyType.production || dataSource.sourceType === EnergyType.consumption,
    );

    const hasActiveProject = user?.projects?.some(isActiveProject);

    if (!hasRequiredDataSources && !hasActiveProject) {
      return false;
    }
  }

  return true;
};

const timeOfDayPreview = (_user?: User, flags?: LDFlags): boolean => {
  return Boolean(flags?.[LDFlagKeys.timeOfDayPreview]) && !PlatformConfig.isLive;
};

const claimPointsArticleWidget = (flags: LDFlags): string => {
  return flags.claimPointsArticleWidget as string;
};

export default {
  service,
  referAFriend,
  demoMode,
  financeAccountDetail,
  displayConnectUtilityOnDashboard,
  featuredItems,
  energyPage,
  energyCarousel,
  achievementsPage,
  solarAndEnergyPage,
  trivia,
  tasks,
  getArticlePoints,
  displayHomePage,
  displayProjectAsHome,
  displayHomePageV2,
  displayNotificationCenter,
  isSingleActiveProjectHome,
  claimPointsArticleWidget,
  connectedAccountsPage,
  splineHouse,
  pointsHistory,
  solarSavings,
  solarValue,
  userEmailUpdate,
  showGetAccountOnLoginSignupChoice,
  riveBottomNav,
  plaidDemo,
  showRateDataForSavings,
  compareMultiplePeriods,
  sunnyChatEnabled,
  timeOfDayPreview,
};
